import React, { useEffect, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { http_get } from '../../utils/api_client';
import { API_ENDPOINTS } from '../../utils/endpoints';

// BL-118 — Notion-style block editor for a Doc section body. Inline text blocks
// you type directly into (Enter makes the next block, Backspace on an empty
// block removes it), a "+" / "/" menu to drop in elements, and drag-reorder.
const DND = 'doc-block';
const genId = () => `blk_${Math.random().toString(36).slice(2, 9)}`;
const TEXT_TYPES = ['text', 'h1', 'h2', 'h3', 'quote', 'callout'];

const BLOCK_TYPES = [
  { type: 'text', label: 'Text', hint: 'Plain paragraph' },
  { type: 'h1', label: 'Heading 1' },
  { type: 'h2', label: 'Heading 2' },
  { type: 'h3', label: 'Heading 3' },
  { type: 'quote', label: 'Quote' },
  { type: 'callout', label: 'Callout' },
  { type: 'divider', label: 'Divider' },
  { type: 'image', label: 'Image', hint: 'By URL' },
  { type: 'video', label: 'Video', hint: 'YouTube, Vimeo, URL' },
  { type: 'embed', label: 'Embed', hint: 'iframe or HTML' },
  { type: 'button', label: 'Button', hint: 'Link button' },
  { type: 'walkthrough', label: 'Walkthrough', hint: 'Interactive walkthrough' }
];

const placeholderFor = (type) => {
  switch (type) {
    case 'h1': return 'Heading 1';
    case 'h2': return 'Heading 2';
    case 'h3': return 'Heading 3';
    case 'quote': return 'Quote';
    case 'callout': return 'Callout';
    default: return "Type '/' for blocks, or just write…";
  }
};

const AutoTextarea = ({ value, placeholder, className, autoFocus, onChange, onEnter, onBackspaceEmpty, onSlash }) => {
  const ref = useRef(null);
  const resize = () => { const el = ref.current; if (el) { el.style.height = 'auto'; el.style.height = `${el.scrollHeight}px`; } };
  useEffect(() => { resize(); }, [value]);
  useEffect(() => {
    if (autoFocus && ref.current) {
      const el = ref.current;
      el.focus();
      const v = el.value;
      el.setSelectionRange(v.length, v.length);
    }
  }, [autoFocus]);

  const onKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onEnter && onEnter(); }
    else if (e.key === 'Backspace' && e.currentTarget.value === '') { e.preventDefault(); onBackspaceEmpty && onBackspaceEmpty(); }
  };
  const onCh = (e) => {
    const v = e.target.value;
    if (v === '/') { onSlash && onSlash(); return; }
    onChange(v);
  };

  return (
    <textarea
      ref={ref}
      className={className}
      rows={1}
      value={value}
      placeholder={placeholder}
      onChange={onCh}
      onKeyDown={onKey}
    />
  );
};

const MediaInput = ({ label, onSet }) => {
  const [v, setV] = useState('');
  return (
    <div className="doc-media-empty">
      <input value={v} placeholder={label} onChange={(e) => setV(e.target.value)} />
      <button type="button" disabled={!v.trim()} onClick={() => onSet(v.trim())}>Add</button>
    </div>
  );
};

// UserNurture-native element: embed a published walkthrough (reuses the Path
// editor's walkthrough search). Stores the chosen walkthrough id/name/slug.
const WalkthroughBlock = ({ block, onChange }) => {
  const [options, setOptions] = useState([]);
  useEffect(() => {
    http_get(API_ENDPOINTS.SEARCH_WALKTHROUGH, { by: 'aasm_state', keyword: 'published' })
      .then(({ data }) => setOptions((data || []).map((w) => ({ id: String(w.id), name: w.name, slug: w.slug }))))
      .catch(() => setOptions([]));
  }, []);

  if (block.source) {
    const w = options.find((o) => o.id === String(block.source));
    return (
      <div className="doc-wt">
        <span className="doc-wt__ic" aria-hidden>▶</span>
        <div className="doc-wt__b">
          <div className="doc-wt__t">{block.walkthrough_name || w?.name || 'Walkthrough'}</div>
          <div className="doc-wt__m">Runs inline for learners</div>
        </div>
        <button type="button" className="doc-wt__change" onClick={() => onChange({ source: '', walkthrough_name: '' })}>Change</button>
      </div>
    );
  }
  return (
    <div className="doc-wt-pick">
      <select
        value=""
        onChange={(e) => {
          const w = options.find((o) => o.id === e.target.value);
          if (w) onChange({ source: w.id, walkthrough_name: w.name });
        }}
      >
        <option value="">Select a walkthrough…</option>
        {options.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
      </select>
    </div>
  );
};

const BlockRow = ({ block, index, focusId, moveBlock, onChange, onEnter, onDelete, onBackspaceEmpty, onOpenMenu }) => {
  const rowRef = useRef(null);
  const gripRef = useRef(null);
  const [{ isDragging }, drag, preview] = useDrag({ type: DND, item: { index }, collect: (m) => ({ isDragging: m.isDragging() }) });
  const [, drop] = useDrop({
    accept: DND,
    hover(dragged) { if (dragged.index !== index) { moveBlock(dragged.index, index); dragged.index = index; } }
  });
  preview(drop(rowRef));
  drag(gripRef);

  const isText = TEXT_TYPES.includes(block.type);

  return (
    <div ref={rowRef} className={`doc-block ${isDragging ? 'is-drag' : ''}`}>
      <div className="doc-block__gutter">
        <button type="button" className="doc-block__add" title="Add block" onClick={() => onOpenMenu(block.id, 'insert')}>＋</button>
        <span ref={gripRef} className="doc-block__grip" title="Drag to reorder">⣿</span>
      </div>
      <div className="doc-block__body">
        {isText && (
          <AutoTextarea
            value={block.text || ''}
            autoFocus={focusId === block.id}
            className={`doc-blk doc-blk--${block.type}`}
            placeholder={placeholderFor(block.type)}
            onChange={(t) => onChange(block.id, { text: t })}
            onEnter={() => onEnter(block.id)}
            onBackspaceEmpty={() => onBackspaceEmpty(block.id)}
            onSlash={() => onOpenMenu(block.id, 'convert')}
          />
        )}
        {block.type === 'divider' && <hr className="doc-hr" />}
        {block.type === 'image' && (
          block.source
            ? <img className="doc-img" src={block.source} alt="" />
            : <MediaInput label="Paste an image URL…" onSet={(v) => onChange(block.id, { source: v })} />
        )}
        {block.type === 'video' && (
          block.source
            ? <div className="doc-mchip">Video added<button type="button" onClick={() => onChange(block.id, { source: '' })}>Change</button></div>
            : <MediaInput label="Paste a video URL (YouTube, Vimeo…)…" onSet={(v) => onChange(block.id, { source: v })} />
        )}
        {block.type === 'embed' && (
          block.source
            ? <div className="doc-mchip">Embed added<button type="button" onClick={() => onChange(block.id, { source: '' })}>Change</button></div>
            : <MediaInput label="Paste embed code (iframe / HTML)…" onSet={(v) => onChange(block.id, { source: v })} />
        )}
        {block.type === 'button' && (
          <div className="doc-btnblk">
            <input className="doc-btnblk__label" value={block.label || ''} placeholder="Button label" onChange={(e) => onChange(block.id, { label: e.target.value })} />
            <input className="doc-btnblk__dest" value={block.destination || ''} placeholder="https://…" onChange={(e) => onChange(block.id, { destination: e.target.value })} />
          </div>
        )}
        {block.type === 'walkthrough' && (
          <WalkthroughBlock block={block} onChange={(p) => onChange(block.id, p)} />
        )}
      </div>
      <button type="button" className="doc-block__rm" title="Delete block" onClick={() => onDelete(block.id)}>✕</button>
    </div>
  );
};

const BlockMenu = ({ onPick, onClose }) => (
  <div className="doc-menu-overlay" onClick={onClose}>
    <div className="doc-menu" onClick={(e) => e.stopPropagation()}>
      <div className="doc-menu__h">Add a block</div>
      {BLOCK_TYPES.map((t) => (
        <div key={t.type} className="doc-menu__item" onClick={() => onPick(t.type)}>
          <span className="doc-menu__l">{t.label}</span>
          {t.hint && <span className="doc-menu__hint">{t.hint}</span>}
        </div>
      ))}
    </div>
  </div>
);

const DocBlocks = ({ blocks, onChange }) => {
  const [focusId, setFocusId] = useState(null);
  const [menu, setMenu] = useState(null); // { blockId, mode: 'insert' | 'convert' }
  const list = blocks || [];

  const setBlock = (id, patch) => onChange(list.map((b) => (b.id === id ? { ...b, ...patch } : b)));

  const newBlock = (type) => ({
    id: genId(),
    type,
    ...(TEXT_TYPES.includes(type) ? { text: '' } : {}),
    ...(['image', 'video', 'embed', 'walkthrough'].includes(type) ? { source: '' } : {}),
    ...(type === 'button' ? { label: 'Button', destination: '' } : {})
  });

  const insertAfter = (id, type) => {
    const nb = newBlock(type);
    const i = list.findIndex((b) => b.id === id);
    const next = [...list];
    next.splice(i < 0 ? list.length : i + 1, 0, nb);
    onChange(next);
    if (TEXT_TYPES.includes(type)) setFocusId(nb.id);
  };

  const convert = (id, type) => {
    onChange(list.map((b) => (b.id === id ? { ...b, type, ...(TEXT_TYPES.includes(type) && b.text === undefined ? { text: '' } : {}) } : b)));
    if (TEXT_TYPES.includes(type)) setFocusId(id);
  };

  const onEnter = (id) => insertAfter(id, 'text');
  const onDelete = (id) => {
    const i = list.findIndex((b) => b.id === id);
    onChange(list.filter((b) => b.id !== id));
    const prev = list[i - 1];
    if (prev && TEXT_TYPES.includes(prev.type)) setFocusId(prev.id);
  };
  const onBackspaceEmpty = (id) => { if (list.length > 1) onDelete(id); };
  const moveBlock = (from, to) => {
    const next = [...list];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m);
    onChange(next);
  };
  const openMenu = (blockId, mode) => setMenu({ blockId, mode });
  const pick = (type) => {
    if (!menu) return;
    if (menu.mode === 'insert') insertAfter(menu.blockId, type); else convert(menu.blockId, type);
    setMenu(null);
  };

  if (list.length === 0) {
    const start = () => { const id = genId(); onChange([{ id, type: 'text', text: '' }]); setFocusId(id); };
    return (
      <div className="doc-blocks">
        <div className="doc-blocks__empty" onClick={start}>Click to start writing, or add a block…</div>
      </div>
    );
  }

  return (
    <div className="doc-blocks">
      {list.map((b, i) => (
        <BlockRow
          key={b.id}
          block={b}
          index={i}
          focusId={focusId}
          moveBlock={moveBlock}
          onChange={setBlock}
          onEnter={onEnter}
          onDelete={onDelete}
          onBackspaceEmpty={onBackspaceEmpty}
          onOpenMenu={openMenu}
        />
      ))}
      {menu && <BlockMenu onPick={pick} onClose={() => setMenu(null)} />}
    </div>
  );
};

export default DocBlocks;
